<template>
  <v-row class="ma-0 flex-grow-1 h-100">
    <v-col cols="12" class="pa-0">
      <v-sheet class="rounded-lg d-flex flex-column px-4" height="100%">
        <v-sheet class="d-flex align-center justify-space-between" color="transparent">
          <v-card-title class="px-0 font-weight-bold" style="color: var(--v-grey-500-base)"
            >Grid</v-card-title
          >

          <v-text-field
            class="flex-grow-1 search-input"
            style="max-width: 360px"
            solo
            dense
            clearable
            hide-details
            v-model="searchText"
            placeholder="Search..."
            append-icon="mdi-magnify"></v-text-field>

          <div class="d-flex align-center">
            <v-btn
              class="mx-4"
              icon
              @click="isolateColoredInspection"
              :loading="coloredInspectionLoading">
              <v-img
                class="mx-2 cursor-pointer"
                width="35"
                height="35"
                src="@Assets/images/color_picker.png"></v-img>
            </v-btn>
            <v-select
              class="customSelectBox"
              :class="{ hasValue: selectedInspectionProperties.length > 0 }"
              style="max-width: 272px"
              v-model="selectedInspectionProperties"
              :items="indexedInspectionProperties"
              @change="updateCheckedStatus"
              placeholder="Select Columns"
              append-icon="mdi-chevron-down"
              item-text="name"
              item-value="name"
              :menu-props="{
                offsetY: true,
                bottom: true,
              }"
              multiple
              solo
              rounded
              dense
              color="primary"
              hide-details>
              <template v-slot:selection="{ index }">
                <div
                  class="text-subtitle-2 font-weight-medium"
                  v-if="index === 0 && selectedInspectionProperties.length > 0">
                  <span class="selectedWord"> Selected Columns</span>
                  <v-chip small color="primary" class="v-chip-custom">{{
                    selectedInspectionProperties.length
                  }}</v-chip>
                </div>
              </template>
              <template v-slot:prepend-inner>
                <img
                  width="22"
                  height="22"
                  src="@Assets/svgs/fluent_column-triple-20-regular.svg"
                  alt="columns icon" />
              </template>
            </v-select>
          </div>
        </v-sheet>
        <ag-grid-vue
          class="gridContainer"
          :class="$vuetify.theme.isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'"
          :gridOptions="gridOptions"
          :defaultColDef="defaultColDef"
          :rowData="rowData"
          @grid-ready="onGridReady"
          @column-moved="columnMovedHandler"
          rowSelection="multiple"
          :rowModelType="rowModelType"
          :cacheBlockSize="cacheBlockSize"
          :cacheOverflowSize="cacheOverflowSize"
          :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
          :infiniteInitialRowCount="infiniteInitialRowCount"
          :maxBlocksInCache="maxBlocksInCache"
          :isRowSelectable="isRowSelectable"
          :getRowId="getRowId"
          @selection-changed="onSelectionChanged"
          :overlayNoRowsTemplate="noRowsTemplate"
          :overlayLoadingTemplate="overlayLoadingTemplate">
        </ag-grid-vue>
      </v-sheet>
    </v-col>
  </v-row>
</template>
<script>
  /* eslint-disable no-unused-vars */
  /* eslint-disable vue/no-unused-components */
  const THREE = window.THREE;
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  import CenterCellRenderer from './sis-datagrid/center-cell-renderer.vue';
  import LinkCellRenderer from './sis-datagrid/link-cell-renderer.vue';
  import ModelMatchRenderer from './sis-datagrid/model-match-renderer.vue';
  import DocumentsCountRenderer from './sis-datagrid/documents-count-renderer.vue';
  import TasksCountRenderer from './sis-datagrid/tasks-count-renderer.vue';
  import { AgGridVue } from 'ag-grid-vue';
  // import hourglassGIF from '../../images/hourglass.gif';
  import cubeLoader from '@public/img/envizion-loader.svg';
  import moment from 'moment';
  import { getInspectionTagsByIds } from '@Utils/amisAPIs';
  import { clearThemingColors, setPermeativeElementsTheme } from '@Utils/forgeUtils';
  import LinkButtonCellRenderer from './sis-datagrid/link-button-cell-renderer.vue';
  import { debounce } from 'lodash';
  import ColorPicker from '@/features/Share/components/ColorPicker.vue';
  import { EventBus } from '@/event-bus';
  import { $getFromLocal, $setToLocal } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'InspectionDataGrid',
    components: {
      AgGridVue,
      CenterCellRenderer,
      LinkCellRenderer,
      LinkButtonCellRenderer,
      ModelMatchRenderer,
      DocumentsCountRenderer,
      TasksCountRenderer,
      ColorPicker,
    },
    props: {
      id: {
        type: String,
        default: 'inspectionGrid',
      },
      selectedFilters: {
        type: Object,
        default: () => {},
      },
      selectedRowsData: {
        type: Array,
        default: () => [],
      },
      coloredInspections: {
        type: Array,
        default: () => [],
      },
      gridName: {
        type: String,
        default: () => 'rbi',
      },
    },
    data() {
      return {
        gridOptions: {
          columnDefs: [],
          pagination: false,
          rowSelection: 'multiple',
          suppressRowClickSelection: true,
          columnTypes: 'columnTypes',
        },
        defaultColDef: {
          width: 200,
          editable: false,
          filter: false,
          sortable: true,
          floatingFilter: false,
          resizable: false,
          cellDataType: false,
          cellRenderer: 'CenterCellRenderer',
        },
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            var dateAsString = cellValue;
            if (dateAsString == null) return -1;
            var dateParts = dateAsString.split('/');
            var cellDate = new Date(
              Number(dateParts[2]),
              Number(dateParts[1]) - 1,
              Number(dateParts[0]),
            );
            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
              return 0;
            }
            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            }
            if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            }
            return 0;
          },
        },

        totalCount: 0,
        columnDefs: null,
        rowData: null,
        gridApi: null,
        columnApi: null,
        rowSelection: null,
        rowModelType: null,
        cacheBlockSize: null,
        cacheOverflowSize: null,
        maxConcurrentDatasourceRequests: null,
        infiniteInitialRowCount: null,
        maxBlocksInCache: null,
        getRowId: null,
        overlayLoadingTemplate: null,
        noRowsTemplate: null,
        loadingIcon: cubeLoader,
        resetGridTimeout: null,
        gridParams: null,
        selectedInspectionProperties: [],
        searchText: '',
        selectedTagColor: null,

        updateColumnVisibilityTimeout: null,
        getColoredInspectionsRequestId: null,
      };
    },
    created() {
      this.rowSelection = 'multiple';
      this.rowModelType = 'infinite';
      this.cacheBlockSize = 100;
      this.cacheOverflowSize = 2;
      this.maxConcurrentDatasourceRequests = 2;
      this.infiniteInitialRowCount = 1;
      this.maxBlocksInCache = 2;
      this.getRowId = (params) => {
        return params.data.id;
      };
      this.overlayLoadingTemplate = `<img style="position:absolute;top:50%;left:50%;transform:translate(-50%, -50%);width:80px;height:80px;" src="${this.loadingIcon}" aria-label="loading">`;
      this.noRowsTemplate =
        '<span style="padding: 15px 30px;border: 2px solid #d3d3d3;background: #e8e8e8;font-size: 18px;color: #6e6e6e; font-weight: bold;">No data to display</span>';
    },
    beforeMount() {
      this.gridOptions.onRowClicked = this.rowClicked;

      var defaultColumnDefs = this.getColumnDefs();

      this.gridOptions.columnDefs = defaultColumnDefs;

      // this.gridOptions.columnDefs = $getFromLocal('columnDefs', true, defaultColumnDefs);
    },
    mounted() {
      this.updateColumnVisibility();
      if (this.inspectionPropertiesWithCheck) {
        this.selectedInspectionProperties = this.inspectionPropertiesWithCheck
          .filter((option) => option.isChecked)
          .map((option) => option.name);
      }
    },
    methods: {
      saveColumnsDefs() {
        var columnDefs = this.gridApi.getColumnDefs();
        $setToLocal(this.columnDefsKey, columnDefs, true);
      },
      columnMovedHandler(e) {
        log(e);
        this.saveColumnsDefs();
      },
      getOptions(params) {
        return {
          sortModel: params.sortModel ? params.sortModel[0] : null,
          pagingModel: {
            startIndex: params.startRow,
            endIndex: params.endRow,
          },
          search: this.searchText,
          ...this.filters,
        };
      },
      onGridReady(params) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.gridParams = params;

        const dataSource = {
          rowCount: undefined,
          getRows: (params) => {
            this.showLoadingPanel();
            var options = this.getOptions(params);
            this.getColoredInspectionsDebounced(options);
            this.$store
              .dispatch('GET_GRID_FILTERED_INSPECTIONS', options)
              .then((result) => {
                if (params.startRow == 0) {
                  this.totalCount = 0;
                  this.$emit('update:selectedRowsData', []);
                  this.gridApi.deselectAll();
                }

                this.totalCount += result.length;

                let lastRow = -1;

                if (result.length == 0 || result.length < params.endRow - params.startRow) {
                  lastRow = this.totalCount;
                }

                params.successCallback(result, lastRow);
              })
              .catch((error) => {
                log(error);
                params.failCallback();
                this.$dialog.notify.error(`Error while fetching data.`, {
                  position: 'top-right',
                  timeout: 3000,
                });
              })
              .finally(() => {
                this.gridApi.hideOverlay();
                if (this.totalCount <= 0) {
                  this.gridApi.showNoRowsOverlay();
                }
              });
          },
        };

        params.api.setDatasource(dataSource);
      },
      showLoadingPanel() {
        this.gridApi.showLoadingOverlay();
      },
      isRowSelectable(rowNode) {
        return (
          rowNode.data?.point &&
          (rowNode.data?.point.x || rowNode.data?.point.y || rowNode.data?.point.z)
        );
      },
      onSelectionChanged(e) {
        let selected = this.gridApi.getSelectedRows();

        this.$emit('update:selectedRowsData', selected);
      },
      dateRenderer(params) {
        if (!params.value) return '-';
        return moment(params.value).format('DD/MM/YYYY');
      },
      cellStyle(params) {
        if (!params.data) return;

        const colId = params.column.colId;

        let currentFilter = this.$store.state.TAGS.InspectionSelectedFilter;
        let property = '';

        if (!currentFilter) return;

        if (currentFilter === 'Corrosion Rate' && colId !== 'cr') return;
        if (currentFilter === 'Remaining Thickness' && colId !== 'rt') return;
        if (currentFilter === 'Remaining Life' && colId !== 'rl') return;

        if (currentFilter === 'Last Inspection (Thickness)' && colId !== 'ispDate') return;
        if (currentFilter === 'Last Inspection (Visual)' && colId !== 'offerLastInspDate') return;
        if (
          currentFilter === 'Last Inspection (Both)' &&
          colId !== 'ispDate' &&
          colId !== 'offerLastInspDate'
        )
          return;

        if (currentFilter === 'Next Inspection (Thickness)' && colId !== 'nIspDate') return;
        if (currentFilter === 'Next Inspection (Visual)' && colId !== 'offerNextInspDate') return;
        if (
          currentFilter === 'Next Inspection (Both)' &&
          colId !== 'nIspDate' &&
          colId !== 'offerNextInspDate'
        )
          return;

        switch (colId) {
          case 'cr':
            property = 'Corrosion Rate';
            break;
          case 'rt':
            property = 'Remaining Thickness';
            break;
          case 'rl':
            property = 'Remaining Life';
            break;
          case 'ispDate':
            property = currentFilter?.includes('Both')
              ? 'Last Inspection (Both)'
              : 'Last Inspection (Thickness)';
            break;
          case 'nIspDate':
            property = currentFilter?.includes('Both')
              ? 'Next Inspection (Both)'
              : 'Next Inspection (Thickness)';
            break;
          case 'offerLastInspDate':
            property = currentFilter?.includes('Both')
              ? 'Last Inspection (Both)'
              : 'Last Inspection (Visual)';
            break;
          case 'offerNextInspDate':
            property = currentFilter?.includes('Both')
              ? 'Next Inspection (Both)'
              : 'Next Inspection (Visual)';
            break;
          default:
            break;
        }

        let color = this.getInspectionColor(params.data, property);

        // return {
        //     backgroundColor: color,
        //     color: this.getBrightness(color) < 130 ? "#fff" : "#000",
        //     fontWeight: 500,
        // };
        return {
          color: color,
          fontWeight: 600,
        };
      },
      getInspectionColor(tag, InspectionSelectedFilter) {
        if (!tag) return;

        let targetProperty = '';
        let selectedColorOptions = [];

        if (!InspectionSelectedFilter) {
          InspectionSelectedFilter = this.$store.state.TAGS.InspectionSelectedFilter;
        }

        switch (InspectionSelectedFilter) {
          case 'Remaining Thickness':
            targetProperty = 'rt';
            selectedColorOptions = this.$store.state.TAGS.InspectionThickPlanColorOptions;
            break;
          case 'Remaining Life':
            targetProperty = 'rl';
            selectedColorOptions = this.$store.state.TAGS.InspectionRemainingLifeColorOptions;
            break;
          case 'Corrosion Rate':
            targetProperty = 'cr';
            selectedColorOptions = this.$store.state.TAGS.InspectionCorrRateColorOptions;
            break;
          case 'Inspection Plan':
            targetProperty = 'ispDateDays';
            selectedColorOptions = this.$store.state.TAGS.InspectionPlanColorOptions;
            break;
          case 'Last Inspection (Thickness)':
            targetProperty = 'ispDateDays';
            selectedColorOptions = this.$store.state.TAGS.LastInspectionColorOptions;
            break;
          case 'Last Inspection (Visual)':
            targetProperty = 'offerLastInspDateDays';
            selectedColorOptions = this.$store.state.TAGS.LastInspectionColorOptions;
            break;
          case 'Last Inspection (Both)':
            targetProperty = 'nearestLastDateDays';
            selectedColorOptions = this.$store.state.TAGS.LastInspectionColorOptions;
            break;
          case 'Next Inspection (Thickness)':
            targetProperty = 'nIspDateDays';
            selectedColorOptions = this.$store.state.TAGS.NextInspectionColorOptions;
            break;
          case 'Next Inspection (Visual)':
            targetProperty = 'offerNextInspDateDays';
            selectedColorOptions = this.$store.state.TAGS.NextInspectionColorOptions;
            break;
          case 'Next Inspection (Both)':
            targetProperty = 'closestNextDateDays';
            selectedColorOptions = this.$store.state.TAGS.NextInspectionColorOptions;
            break;
          default:
            break;
        }

        return this.$store.state.forgeService.getAMISTagColor(
          tag,
          selectedColorOptions,
          targetProperty,
          '#9a5454',
        );
      },
      getBrightness(color) {
        const r = parseInt(color.substr(1, 2), 16);
        const g = parseInt(color.substr(3, 2), 16);
        const b = parseInt(color.substr(5, 2), 16);
        return (r * 299 + g * 587 + b * 114) / 1000;
      },
      dateMonthYearRenderer(params) {
        if (!params.value) return '-';
        return moment(params.value).format('MM/YYYY');
      },
      reset() {
        log(`%cResetting grid ${this.id}...`, 'color: #ff0000; font-size:14px;');

        if (this.resetGridTimeout) clearTimeout(this.resetGridTimeout);

        this.resetGridTimeout = setTimeout(() => {
          clearTimeout(this.resetGridTimeout);
          this.gridApi.infiniteRowModel.reset();
        }, 1000);
      },
      rowClicked(row) {
        this.$emit('tagSelected');
        if (!row.data.point) return;

        this.gridApi.deselectAll();

        row.node.setSelected(true);

        setTimeout(() => {
          this.isolateColoredInspection();
        }, 10);
      },
      isolateColoredInspection() {
        this.$store.commit('SET_VIEWER_TYPE', '3d');
        this.$emit('openViewer');
      },
      isolateSelectedInspection3d() {
        let tree = this.$store.state.viewer?.model?.getInstanceTree();

        if (!tree) {
          this.$dialog.notify.warning('Model not loaded yet.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.$store.dispatch('ISOLATE_COLORED_INSPECTIONS', this.selectedColoredInspections);
      },
      isolateInspection(data) {
        if (!data || !data.length) return;

        let inspection = data[0];

        if (!inspection.point) {
          this.$dialog.notify.warning('No inspections found with the filtered items.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        let tree = this.$store.state.viewer?.model?.getInstanceTree();

        if (!tree) {
          this.$dialog.notify.warning('Model not loaded yet.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        let themedDbIds = this.$store.getters['GET_THEMED_DB_IDS'];
        let needToReset = this.$store.state.isGhostingOn && !this.$store.state.isGhostingWireframe;

        if (!!themedDbIds?.length && needToReset) clearThemingColors(this.$store.state.viewer);

        getInspectionTagsByIds([inspection.id]).then((tags) => {
          this.$store.state.forgeService.updateAMISTagsColors(tags);

          let dbIdsAMIS = [];

          tags.forEach((tag) => {
            let dbIds = tag.children.map((t) => t.dbId) ?? [];

            dbIdsAMIS.push(...dbIds);

            let color = tag.pinColor;
            color = new THREE.Color(color);
            color = new THREE.Vector4(color.r, color.g, color.b, 1);
            setPermeativeElementsTheme(dbIds, color);
          });

          this.$store.commit('SET_THEMED_DB_IDS', dbIdsAMIS);

          this.$store.state.forgeService
            .getMainExtension()
            .isolateDbIds(dbIdsAMIS, this.$store.state.viewer.model);
        });
      },
      openInspectionThicknessPage(data) {
        if (!data.thickUrl) {
          this.$dialog.notify.warning('No thickness history found with the inspection tag.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        window.open(data.thickUrl, '_blank');
      },
      updateColumnVisibilityHandler() {
        if (this.updateColumnVisibilityTimeout) clearTimeout(this.updateColumnVisibilityTimeout);

        this.updateColumnVisibilityTimeout = setTimeout(() => {
          this.updateColumnVisibility();
        }, 500);
      },
      updateColumnVisibility() {
        const columnApi = this.gridOptions.columnApi.columnModel;
        const amisAdditionalColumns = [
          'A-MIS Link',
          'Base specification Url',
          'Thickness history Url',
        ];

        this.gridOptions.columnDefs.forEach((c) => {
          let o = this.inspectionPropertiesWithCheck?.find((f) => f.name == c.headerName);

          if (c.headerName === 'A-MIS Link') {
            columnApi.setColumnVisible(c.colId, this.isAMIS);

            return;
          }

          let visibility = o?.isChecked ?? false;

          // if (!this.isAMIS && amisAdditionalColumns.includes(c.headerName)) visibility = false;

          if (o) columnApi.setColumnVisible(c.colId, visibility);
        });
      },
      // onFilterTextBoxChanged(searchTxt) {
      //   this.gridOptions.setQuickFilter(searchTxt);
      // },
      getColoredInspectionsDebounced: debounce(function (options) {
        // let options=this.getOptions(this.gridParams);
        if (options.pagingModel.startIndex == 0 || !options.pagingModel.startIndex) {
          let id = new Date().getTime();
          this.getColoredInspectionsRequestId = id;
          this.$store.dispatch('GET_COLORED_INSPECTIONS', options).then((coloredInspections) => {
            if (this.getColoredInspectionsRequestId != id) return;
            this.$emit('update:coloredInspections', coloredInspections);
          });
        }
      }, 1000),
      onFilterTextBoxChanged: debounce(function () {
        this.onGridReady(this.gridParams);
      }, 1500),

      updateCheckedStatus() {
        this.inspectionPropertiesWithCheck.forEach((item) => {
          item.isChecked = this.selectedInspectionProperties.includes(item.name);
        });
        $setToLocal(this.columnDefsKeyChecked, this.inspectionPropertiesWithCheck, true);
        this.updateColumnVisibilityHandler();
      },
      getColumnDefs() {
        var result = null;
        //#region
        switch (this.gridName) {
          case 'rbi':
            result = [
              {
                headerName: '',
                colId: 'id',
                maxWidth: 50,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: false,
                suppressMenu: true,
                suppressMovable: true,
                suppressFilter: true,
                suppressSorting: true,
                pinned: 'left',
              },
              {
                headerName: '3D Model',
                field: 'point',
                colId: 'point',
                pinned: 'left',
                maxWidth: 100,
                sortable: true,
                cellRenderer: 'ModelMatchRenderer',
              },

              {
                headerName: 'Inspection',
                field: 'name',
                colId: 'name',
                hide: this.isAMIS,
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              {
                headerName: 'Name',
                field: 'lno',
                colId: 'lno',
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              { headerName: 'Documents', maxWidth: 120, field: 'documentsCount', colId: 'documentsCount', cellRenderer: 'DocumentsCountRenderer', },
              { headerName: 'Tasks', maxWidth: 80, field: 'tasksCount', colId: 'tasksCount', cellRenderer: 'TasksCountRenderer', },
              { headerName: 'Plant', field: 'm_Plant', colId: 'm_Plant' },
              { headerName: 'Unit', field: 'm_Unit', colId: 'm_Unit' },
              {
                headerName: 'Operating Temperature',
                field: 'm_OperatingTemperature',
                colId: 'm_OperatingTemperature',
              },
              {
                headerName: 'Operating Pressure',
                field: 'm_OperatingPressure',
                colId: 'm_OperatingPressure',
              },
              {
                headerName: 'Functional Location',
                field: 'm_FunctionalLocation',
                colId: 'm_FunctionalLocation',
              },
              { headerName: 'Code', field: 'code', colId: 'code' },
              { headerName: 'COVE', field: 'm_Cove', colId: 'm_Cove' },
              { headerName: 'Corrosion Loop', field: 'clNo1', colId: 'clNo1' },
              {
                headerName: 'FRBI Analysis Created Date',
                field: 'frbiAnalysisCreatedDate',
                colId: 'frbiAnalysisCreatedDate',
              },
              {
                headerName: 'Target Degradation Factor (DF)',
                field: 'targetDfDate',
                colId: 'targetDfDate',
              },
              {
                headerName: 'Inspection Report Created',
                field: 'createdDate',
                colId: 'createdDate',
              },
              { headerName: 'Current Risk', field: 'currentRisk', colId: 'currentRisk' },
              {
                headerName: 'Future Risk w/o Inspection',
                field: 'futureRisk',
                colId: 'futureRisk',
              },
              {
                headerName: 'Future Risk w/ Inspection',
                field: 'futureRiskInspections',
                colId: 'futureRiskInspections',
              },
              {
                headerName: 'Design Max Pressure',
                field: 'm_DesignMaxPressure',
                colId: 'm_DesignMaxPressure',
              },
              {
                headerName: 'Design Min Pressure',
                field: 'm_DesignMinPressure',
                colId: 'm_DesignMinPressure',
              },
              {
                headerName: 'Design Max Temp ',
                field: 'm_DesignMaxTemperature',
                colId: 'm_DesignMaxTemperature',
              },
              {
                headerName: 'Design Min Temp ',
                field: 'm_DesignMinTemperature',
                colId: 'm_DesignMinTemperature',
              },
              {
                headerName: 'Criticality',
                field: 'm_StressCriticality',
                colId: 'm_StressCriticality',
              },
              {
                headerName: 'Insulation Temperature',
                field: 'm_TempInsulTemp',
                colId: 'm_TempInsulTemp',
              },
            ];
            break;

          case 'inspection':
            result = [
              {
                headerName: '',
                colId: 'id',
                maxWidth: 50,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: false,
                suppressMenu: true,
                suppressMovable: true,
                suppressFilter: true,
                suppressSorting: true,
                pinned: 'left',
              },
              {
                headerName: '3D Model',
                field: 'point',
                colId: 'point',
                pinned: 'left',
                maxWidth: 100,
                sortable: true,
                cellRenderer: 'ModelMatchRenderer',
              },

              {
                headerName: 'Inspection',
                field: 'name',
                colId: 'name',
                hide: this.isAMIS,
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              {
                headerName: 'Name',
                field: 'lno',
                colId: 'lno',
                sortable: true,
                filter: false,
                pinned: 'left',
              },

              { headerName: 'Documents', maxWidth: 120, field: 'documentsCount', colId: 'documentsCount', cellRenderer: 'DocumentsCountRenderer', },
              { headerName: 'Tasks', maxWidth: 80, field: 'tasksCount', colId: 'tasksCount', cellRenderer: 'TasksCountRenderer', },
              { headerName: 'Plant', field: 'm_Plant', colId: 'm_Plant' },
              { headerName: 'Unit', field: 'm_Unit', colId: 'm_Unit' },
              { headerName: 'Asset Class', field: 'assetClass', colId: 'assetClass' },
              {
                headerName: 'Operating Temperature',
                field: 'm_OperatingTemperature',
                colId: 'm_OperatingTemperature',
              },
              {
                headerName: 'Operating Pressure',
                field: 'm_OperatingPressure',
                colId: 'm_OperatingPressure',
              },
              {
                headerName: 'Functional Location',
                field: 'm_FunctionalLocation',
                colId: 'm_FunctionalLocation',
              },
              { headerName: 'Code', field: 'code', colId: 'code' },
              { headerName: 'COVE', field: 'm_Cove', colId: 'm_Cove' },
              { headerName: 'Corrosion Loop', field: 'clNo1', colId: 'clNo1' },

              {
                headerName: 'Inspection Report Created',
                field: 'createdDate',
                colId: 'createdDate',
              },
              {
                headerName: 'Inspection Report Approved',
                field: 'createdDate',
                colId: 'createdDate',
              },
              { headerName: 'Inspection Type', field: 'type', colId: 'type' },
              { headerName: 'WO No.', field: 'workPackNumber', colId: 'workPackNumber' },
              { headerName: 'Status', field: 'status', colId: 'status' },
              {
                headerName: 'PRD Overhaul ',
                field: 'm_PrdOverhaulDate',
                colId: 'm_PrdOverhaulDate',
              },
              { headerName: 'MDMT', field: 'm_MDMT', colId: 'm_MDMT' },

              {
                headerName: 'Design Max Pressure',
                field: 'm_DesignMaxPressure',
                colId: 'm_DesignMaxPressure',
              },
              {
                headerName: 'Design Min Pressure',
                field: 'm_DesignMinPressure',
                colId: 'm_DesignMinPressure',
              },
              {
                headerName: 'Design Max Temp ',
                field: 'm_DesignMaxTemperature',
                colId: 'm_DesignMaxTemperature',
              },
              {
                headerName: 'Design Min Temp ',
                field: 'm_DesignMinTemperature',
                colId: 'm_DesignMinTemperature',
              },
              {
                headerName: 'Criticality',
                field: 'm_StressCriticality',
                colId: 'm_StressCriticality',
              },
              {
                headerName: 'Insulation Temperature',
                field: 'm_TempInsulTemp',
                colId: 'm_TempInsulTemp',
              },
            ];
            break;
          case 'thickness':
            result = [
              {
                headerName: '',
                colId: 'id',
                maxWidth: 50,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: false,
                suppressMenu: true,
                suppressMovable: true,
                suppressFilter: true,
                suppressSorting: true,
                pinned: 'left',
              },
              {
                headerName: '3D Model',
                field: 'point',
                colId: 'point',
                pinned: 'left',
                maxWidth: 100,
                sortable: true,
                cellRenderer: 'ModelMatchRenderer',
              },

              {
                headerName: 'Inspection',
                field: 'name',
                colId: 'name',
                hide: this.isAMIS,
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              {
                headerName: 'Name',
                field: 'lno',
                colId: 'lno',
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              { headerName: 'Documents', maxWidth: 120, field: 'documentsCount', colId: 'documentsCount', cellRenderer: 'DocumentsCountRenderer', },
              { headerName: 'Tasks', maxWidth: 80, field: 'tasksCount', colId: 'tasksCount', cellRenderer: 'TasksCountRenderer', },
              { headerName: 'Plant', field: 'm_Plant', colId: 'm_Plant' },
              { headerName: 'Unit', field: 'm_Unit', colId: 'm_Unit' },
              { headerName: 'Asset Class', field: 'assetClass', colId: 'assetClass' },
              {
                headerName: 'Operating Temperature',
                field: 'm_OperatingTemperature',
                colId: 'm_OperatingTemperature',
              },
              {
                headerName: 'Operating Pressure',
                field: 'm_OperatingPressure',
                colId: 'm_OperatingPressure',
              },
              {
                headerName: 'Functional Location',
                field: 'm_FunctionalLocation',
                colId: 'm_FunctionalLocation',
              },
              { headerName: 'Code', field: 'code', colId: 'code' },
              { headerName: 'COVE', field: 'm_Cove', colId: 'm_Cove' },
              { headerName: 'Corrosion Loop', field: 'clNo1', colId: 'clNo1' },

              { headerName: 'Corrosion Rate', field: 'cr', colId: 'cr' },
              { headerName: 'Remaining Thickness', field: 'rt', colId: 'rt' },
              { headerName: 'Remaining Life', field: 'rl', colId: 'rl' },
              { headerName: 'End of Life', field: 'el', colId: 'el' },
              { headerName: 'Minimum Thickness', field: 'minThickness', colId: 'minThickness' },

              {
                headerName: 'Measurement Thickness',
                field: 'measuredThickness',
                colId: 'measuredThickness',
              },
              {
                headerName: 'Last Inspection Date',
                field: 'offerLastInspDate',
                colId: 'offerLastInspDate',
              },
              { headerName: 'Next Inspection', field: 'nIspDate', colId: 'nIspDate' },
              {
                headerName: 'VI/NDI Inspection Date ',
                field: 'offerLastInspDate',
                colId: 'offerLastInspDate',
              },
              {
                headerName: 'VI/NDI Next Inspection Date ',
                field: 'offerNextInspDate',
                colId: 'offerNextInspDate',
              },

              {
                headerName: 'Design Max Pressure',
                field: 'm_DesignMaxPressure',
                colId: 'm_DesignMaxPressure',
              },
              {
                headerName: 'Design Min Pressure',
                field: 'm_DesignMinPressure',
                colId: 'm_DesignMinPressure',
              },
              {
                headerName: 'Design Max Temp ',
                field: 'm_DesignMaxTemperature',
                colId: 'm_DesignMaxTemperature',
              },
              {
                headerName: 'Design Min Temp ',
                field: 'm_DesignMinTemperature',
                colId: 'm_DesignMinTemperature',
              },
              {
                headerName: 'Criticality',
                field: 'm_StressCriticality',
                colId: 'm_StressCriticality',
              },
              {
                headerName: 'Insulation Temperature',
                field: 'm_TempInsulTemp',
                colId: 'm_TempInsulTemp',
              },
            ];
            break;

          case 'cathodic':
            result = [
              {
                headerName: '',
                colId: 'id',
                maxWidth: 50,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: false,
                suppressMenu: true,
                suppressMovable: true,
                suppressFilter: true,
                suppressSorting: true,
                pinned: 'left',
              },
              {
                headerName: '3D Model',
                field: 'point',
                colId: 'point',
                pinned: 'left',
                maxWidth: 100,
                sortable: true,
                cellRenderer: 'ModelMatchRenderer',
              },

              {
                headerName: 'Inspection',
                field: 'name',
                colId: 'name',
                hide: this.isAMIS,
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              {
                headerName: 'Name',
                field: 'lno',
                colId: 'lno',
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              { headerName: 'Documents', maxWidth: 120, field: 'documentsCount', colId: 'documentsCount', cellRenderer: 'DocumentsCountRenderer', },
              { headerName: 'Tasks', maxWidth: 80, field: 'tasksCount', colId: 'tasksCount', cellRenderer: 'TasksCountRenderer', },
              { headerName: 'Plant', field: 'm_Plant', colId: 'm_Plant' },
              { headerName: 'Unit', field: 'm_Unit', colId: 'm_Unit' },
              { headerName: 'Asset Class', field: 'assetClass', colId: 'assetClass' },
              {
                headerName: 'Operating Temperature',
                field: 'm_OperatingTemperature',
                colId: 'm_OperatingTemperature',
              },
              {
                headerName: 'Operating Pressure',
                field: 'm_OperatingPressure',
                colId: 'm_OperatingPressure',
              },
              {
                headerName: 'Functional Location',
                field: 'm_FunctionalLocation',
                colId: 'm_FunctionalLocation',
              },
              { headerName: 'Code', field: 'code', colId: 'code' },
              { headerName: 'COVE', field: 'm_Cove', colId: 'm_Cove' },
              { headerName: 'Corrosion Loop', field: 'clNo1', colId: 'clNo1' },

              {
                headerName: 'Cathodic Protection Start Date',
                field: 'cathodicProtectionStartDate',
                colId: 'cathodicProtectionStartDate',
              },
              {
                headerName: 'Cathodic Protection End Date',
                field: 'cathodicProtectionEndDate',
                colId: 'cathodicProtectionEndDate',
              },
              {
                headerName: 'Cathodic Protection Status',
                field: 'cathodicProtectionStatus',
                colId: 'cathodicProtectionStatus',
              },
              {
                headerName: 'Protected/Un-protected',
                field: 'protectedUnprotected',
                colId: 'protectedUnprotected',
              },

              { headerName: 'MDMT', field: 'm_MDMT', colId: 'm_MDMT' },
              {
                headerName: 'Design Max Pressure',
                field: 'm_DesignMaxPressure',
                colId: 'm_DesignMaxPressure',
              },
              {
                headerName: 'Design Min Pressure',
                field: 'm_DesignMinPressure',
                colId: 'm_DesignMinPressure',
              },
              {
                headerName: 'Design Max Temp ',
                field: 'm_DesignMaxTemperature',
                colId: 'm_DesignMaxTemperature',
              },
              {
                headerName: 'Design Min Temp ',
                field: 'm_DesignMinTemperature',
                colId: 'm_DesignMinTemperature',
              },
              {
                headerName: 'Criticality',
                field: 'm_StressCriticality',
                colId: 'm_StressCriticality',
              },
              {
                headerName: 'Insulation Temperature',
                field: 'm_TempInsulTemp',
                colId: 'm_TempInsulTemp',
              },
            ];

            break;
          default:
            result = [
              {
                headerName: '',
                colId: 'id',
                maxWidth: 50,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: false,
                suppressMenu: true,
                suppressMovable: true,
                suppressFilter: true,
                suppressSorting: true,
                pinned: 'left',
              },
              {
                headerName: '3D Model',
                field: 'point',
                colId: 'point',
                pinned: 'left',
                maxWidth: 100,
                sortable: true,
                cellRenderer: 'ModelMatchRenderer',
              },
              {
                headerName: 'A-MIS Link',
                field: 'thickUrl',
                colId: 'thickUrlBtn',
                pinned: 'left',
                maxWidth: 150,
                headerClass: 'text-center',
                cellRenderer: 'LinkButtonCellRenderer',
                cellRendererParams: {
                  clicked: (tag) => {
                    this.openInspectionThicknessPage(tag);
                  },
                },
              },
              {
                headerName: 'Inspection',
                field: 'name',
                colId: 'name',
                hide: this.isAMIS,
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              {
                headerName: 'Name',
                field: 'lno',
                colId: 'lno',
                sortable: true,
                filter: false,
                pinned: 'left',
              },
              { headerName: 'Tag No.', field: 'lnoCode', colId: 'lnoCode', maxWidth: 100 },
              {
                headerName: 'Cr',
                field: 'cr',
                colId: 'cr',
                maxWidth: 100,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'Rt',
                field: 'rt',
                colId: 'rt',
                maxWidth: 100,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'Rl',
                field: 'rl',
                colId: 'rl',
                maxWidth: 100,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'El',
                field: 'el',
                colId: 'el',
                maxWidth: 150,
                sortable: true,
                cellRenderer: this.dateMonthYearRenderer,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'Last Inspection Date',
                field: 'ispDate',
                colId: 'ispDate',
                sortable: true,
                cellRenderer: this.dateRenderer,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'Next Inspection Date',
                field: 'nIspDate',
                colId: 'nIspDate',
                sortable: true,
                cellRenderer: this.dateRenderer,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'VI/NDI Inspection Date',
                field: 'offerLastInspDate',
                colId: 'offerLastInspDate',
                sortable: true,
                cellRenderer: this.dateRenderer,
                cellStyle: this.cellStyle,
              },
              {
                headerName: 'VI/NDI Next Inspection Date',
                field: 'offerNextInspDate',
                colId: 'offerNextInspDate',
                sortable: true,
                cellRenderer: this.dateRenderer,
                cellStyle: this.cellStyle,
              },
              { headerName: 'General regulations', field: 'lawName', colId: 'lawName' },
              { headerName: 'Code 1', field: 'lawName1', colId: 'lawName1' },
              { headerName: 'Code 2', field: 'lawName2', colId: 'lawName2' },
              { headerName: 'Code 3', field: 'lawName3', colId: 'm_OperatingTemperature' },
              { headerName: 'Temperature 1', field: 'temperature1', colId: 'temperature1' },
              { headerName: 'Temperature 2', field: 'temperature2', colId: 'temperature2' },
              { headerName: 'Temperature 3', field: 'temperature3', colId: 'temperature3' },
              { headerName: 'Temperature 4', field: 'temperature4', colId: 'temperature4' },
              {
                headerName: 'Temperature Unit',
                field: 'temperatureUnit',
                colId: 'temperatureUnit',
              },
              { headerName: 'Pressure 1', field: 'pressure1', colId: 'pressure1' },
              { headerName: 'Pressure 2', field: 'pressure2', colId: 'pressure2' },
              { headerName: 'Pressure 3', field: 'pressure3', colId: 'pressure3' },
              { headerName: 'Pressure 4', field: 'pressure4', colId: 'pressure4' },
              { headerName: 'Pressure Unit', field: 'pressureUnit', colId: 'pressureUnit' },
              { headerName: 'Category', field: 'ctgName', colId: 'ctgName' },
              { headerName: 'CL No.', field: 'clName', colId: 'clName' },
              { headerName: 'Corrosion Loop No.1', field: 'clNo1', colId: 'clNo1' },
              { headerName: 'Corrosion Loop No.2', field: 'clNo2', colId: 'clNo2' },
              { headerName: 'Date of production', field: 'makDate', colId: 'makDate' },
              {
                headerName: 'Fluid Classification 1',
                field: 'fluidClassification1',
                colId: 'fluidClassification1',
              },
              {
                headerName: 'Fluid Classification 2',
                field: 'fluidClassification2',
                colId: 'fluidClassification2',
              },
              { headerName: 'Flow Class', field: 'flwClsName', colId: 'flwClsName' },
              { headerName: 'Flow name', field: 'flwName', colId: 'flwName' },
              { headerName: 'Form', field: 'stName', colId: 'stName' },
              { headerName: 'From', field: 'macFrom', colId: 'macFrom' },
              { headerName: 'Function', field: 'fncName', colId: 'fncName' },
              { headerName: 'Importance', field: 'impName', colId: 'impName' },
              { headerName: 'Manufacturer', field: 'mkrName', colId: 'mkrName' },
              { headerName: 'To', field: 'macTo', colId: 'macTo' },
              { headerName: 'Type', field: 'macType', colId: 'macType' },
              { headerName: 'Utilization status', field: 'useCase', colId: 'useCase' },

              { headerName: 'Asset Class', field: 'assetClass', colId: 'assetClass' },
              { headerName: 'Criticality', field: 'criticality', colId: 'criticality' },
              { headerName: 'Current Risk', field: 'currentRisk', colId: 'currentRisk' },
              {
                headerName: 'Future Risk w/o Inspections',
                field: 'futureRisk',
                colId: 'futureRisk',
              },
              {
                headerName: 'Future Risk w/ Inspections',
                field: 'futureRiskInspections',
                colId: 'futureRiskInspections',
              },
              // { headerName: 'End of Life', field: 'el', colId: 'el' },
              { headerName: 'Func Location', field: 'funcLocation', colId: 'funcLocation' },
              { headerName: 'Failure Type', field: 'failureType', colId: 'failureType' },
              { headerName: 'Failure Date', field: 'failureDate', colId: 'failureDate' },
              {
                headerName: 'Inspection Report Created Date',
                field: 'createdDate',
                colId: 'createdDate',
              },
              {
                headerName: 'Inspection Report Issued Date',
                field: 'issuedDate',
                colId: 'issuedDate',
              },
              {
                headerName: 'Inspection Recommendation Date',
                field: 'recommendationDate',
                colId: 'recommendationDate',
              },
              {
                headerName: 'Inspection Results Approval Status',
                field: 'resultsApprovalStatus',
                colId: 'resultsApprovalStatus',
              },
              {
                headerName: 'Inspection Results Excess Corrosion',
                field: 'resultsExcessCorrosion',
                colId: 'resultsExcessCorrosion',
              },
              {
                headerName: 'Inspection Results Pending Report',
                field: 'resultsPendingReport',
                colId: 'resultsPendingReport',
              },
              {
                headerName: 'Inspection Iso Number',
                field: 'inspectionIsoNumber',
                colId: 'inspectionIsoNumber',
              },
              {
                headerName: 'Inspection Iso Revision Number',
                field: 'inspectionIsoRevisionNumber',
                colId: 'inspectionIsoRevisionNumber',
              },
              {
                headerName: 'IOW Excursion of Limits',
                field: 'iowExcursionOfLimits',
                colId: 'iowExcursionOfLimits',
              },
              {
                headerName: 'Measured Thickness',
                field: 'measuredThickness',
                colId: 'measuredThickness',
              },
              { headerName: 'Min Thickness', field: 'minThickness', colId: 'minThickness' },
              {
                headerName: 'P&ID Drawing Number',
                field: 'pIdDrawingNumber',
                colId: 'pIdDrawingNumber',
              },
              {
                headerName: 'P&ID Revision Number',
                field: 'pIdRevisionNumber',
                colId: 'pIdRevisionNumber',
              },
              { headerName: 'Recommendation', field: 'recommendation', colId: 'recommendation' },
              {
                headerName: 'Risk Matrix Rating',
                field: 'riskMatrixRating',
                colId: 'riskMatrixRating',
              },
              { headerName: 'System', field: 'system', colId: 'system' },
              { headerName: 'Subsystem', field: 'subsystem', colId: 'subsystem' },
              { headerName: 'Severity', field: 'severity', colId: 'severity' },
              { headerName: 'Target DF Date', field: 'targetDfDate', colId: 'targetDfDate' },
              { headerName: 'Tag Description', field: 'tagDescription', colId: 'tagDescription' },
              {
                headerName: 'Cathodic Protection Start Date',
                field: 'cathodicProtectionStartDate',
                colId: 'cathodicProtectionStartDate',
              },
              {
                headerName: 'Cathodic Protection End Date',
                field: 'cathodicProtectionEndDate',
                colId: 'cathodicProtectionEndDate',
              },
              {
                headerName: 'Cathodic Protection Status',
                field: 'cathodicProtectionStatus',
                colId: 'cathodicProtectionStatus',
              },
              {
                headerName: 'Protected/Un-Protected',
                field: 'protectedUnprotected',
                colId: 'protectedUnprotected',
              },
              { headerName: 'Documents', maxWidth: 120, field: 'documentsCount', colId: 'documentsCount', cellRenderer: 'DocumentsCountRenderer', },
              { headerName: 'Tasks', maxWidth: 80, field: 'tasksCount', colId: 'tasksCount', cellRenderer: 'TasksCountRenderer', },
              { headerName: 'Plant', field: 'plant', colId: 'plant' },
              {
                headerName: 'Equipment Start Date',
                field: 'equipmentStartDate',
                colId: 'equipmentStartDate',
              },
              {
                headerName: 'Equipment End Date',
                field: 'equipmentEndDate',
                colId: 'equipmentEndDate',
              },
            ];
            break;
        }
        //#endregion
        return $getFromLocal(this.columnDefsKey, true, result);
      },
    },
    computed: {
      columnDefsKey() {
        return 'columnDefs-' + this.gridName;
      },
      columnDefsKeyChecked() {
        return 'columnDefs-' + this.gridName + '-checked';
      },
      generalFilters: {
        get() {
          return this.$store.getters['DASHBOARD_FILTERS/GET_FILTERS'];
        },
      },
      coloredInspectionLoading() {
        return this.$store.getters['GET_COLORED_INSPECTION_LOADING'];
      },
      isAMIS() {
        return !!this.$store.state.forgeSettings?.isAMIS;
      },
      isDarkModeOn: {
        get() {
          return this.$store.getters['SETTING/GET_DARK_MODE'];
        },
        set(value) {
          this.$store.commit('SET_DARK_MODE', value);
        },
      },
      theme() {
        return this.isDarkModeOn ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';
      },
      inspectionFiltersOptionsWithCheck() {
        return this.$store.state.TAGS.inspectionFiltersOptionsWithCheck;
      },
      inspectionSelectedFilter: {
        get() {
          return this.inspectionFiltersOptionsWithCheck.find((o) => o.isChecked);
        },
      },
      // units() {
      //   let units = this.$store.getters['GET_AMIS_UNITS'];
      //   let result = units.map((o) => o);
      //   result.splice(0, 0, {
      //     id: 0,
      //     name: 'Select All',
      //     isChecked: !units.some((o) => !o.isChecked),
      //   });
      //   return result;
      // },
      // selectedUnitsName() {
      //   let selectedUnits = this.units.filter((f) => f.isChecked && f.name != 'Select All');
      //   return selectedUnits.map((f) => f.name);
      // },
      // inspectionTempPresSelectedFilter: {
      //   get() {
      //     return this.$store.getters.GET_INSPECTION_TEMP_PRES_SELECTED_FILTER;
      //   },
      // },
      // selectedExtraFilterOptions() {
      //   if (this.inspectionTempPresSelectedFilter == 'Temperature') {
      //     return this.$store.getters.GET_INSPECTION_TEMPERATURE_OPTIONS.filter((t) => t.isChecked);
      //   } else if (this.inspectionTempPresSelectedFilter == 'Pressure') {
      //     return this.$store.getters.GET_INSPECTION_PRESSURE_OPTIONS.filter((t) => t.isChecked);
      //   } else {
      //     return [];
      //   }
      // },
      // inspectionCodesOptions: {
      //   get() {
      //     return this.$store.getters.GET_INSPECTION_CODES_OPTIONS.filter((f) => f.isChecked);
      //   },
      // },
      // selectedCodesIds() {
      //   return this.inspectionCodesOptions.map((o) => o.id);
      // },
      inspectionType: {
        get: function () {
          return this.$store.state.TAGS.inspectionType;
        },
      },
      selectedColorOptions: {
        get: function () {
          return this.$store.state.TAGS.selectedColorOptions;
        },
      },
      dispatchResetDashboardGrid() {
        return this.$store.getters.GET_RESET_DASHBOARD_GRID;
      },
      inspectionPropertiesWithCheck: {
        get() {
          var headers = this.getColumnDefs();
          var result = headers.map((e) => ({ name: e.headerName, isChecked: true }));
          result.splice(0, 1);
          return $getFromLocal(this.columnDefsKeyChecked, true, result);
        },
        set(value) {
          $setToLocal(this.columnDefsKeyChecked, value, true);
        },
      },
      filters() {
        let convertInf = (value) => {
          if (value == '-∞') return -10e9;
          if (value == '∞') return 10e9;
          return value;
        };

        let filters = {
          facilityId: this.$route.params.id,
          ...this.selectedFilters,
          ...this.generalFilters,
          temperature:
            this.generalFilters.temperature?.map((o) => {
              return {
                min: convertInf(o.judgCrr2),
                max: convertInf(o.judgCrr1),
              };
            }) || [],
          pressure:
            this.generalFilters.pressure?.map((o) => {
              return {
                min: convertInf(o.judgCrr2),
                max: convertInf(o.judgCrr1),
              };
            }) || [],
        };

        return filters;
      },
      visibleColumns() {
        if (this.inspectionPropertiesWithCheck) {
          return this.inspectionPropertiesWithCheck.filter((f) => f.isChecked);
        } else {
          return [];
        }
      },
      indexedInspectionProperties() {
        if (this.inspectionPropertiesWithCheck) {
          return this.inspectionPropertiesWithCheck.map((item, index) => ({
            ...item,
            index,
          }));
        } else {
          return [];
        }
      },
      selectedColoredInspections() {
        if (!this.selectedRowsData.length) return this.coloredInspections;

        return this.coloredInspections.filter((o) =>
          this.selectedRowsData.some((s) => s.id === o.id),
        );
      },
    },
    watch: {
      selectedColorOptions() {
        this.reset();
      },
      dispatchResetDashboardGrid() {
        this.reset();
      },
      visibleColumns: {
        immediate: true,
        handler: function () {
          this.updateColumnVisibilityHandler();
        },
        deep: true,
      },
      searchText() {
        this.onFilterTextBoxChanged();
      },
      filters: {
        handler: function (val, old) {
          if (this.gridParams && JSON.stringify(old) !== JSON.stringify(val)) {
            this.onGridReady(this.gridParams);
          }
        },
        deep: true,
      },
    },
  };
</script>
<style lang="scss">
  .ag-root.ag-layout-normal {
    border: 1px solid var(--v-light-grey-base);
    border-radius: 8px;
  }

  .grid-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 !important;
  }

  .grid-container input {
    height: auto;
    font-weight: 400;
    color: inherit !important;
  }

  .ag-theme-alpine,
  .ag-theme-alpine-dark {
    --ag-range-selection-border-color: transparent !important;
    font-family: 'Poppins', sans-serif;

    --ag-selected-row-background-color: #03624c24;
    --ag-alpine-active-color: #03624c;
    --ag-row-hover-color: #03624c24;
    --ag-input-focus-border-color: #03624c76;
    .ag-row-hover {
      &::before {
        background-color: var(--ag-row-hover-color) !important;
      }
    }
  }

  .ag-ltr .ag-cell,
  .ag-header-cell,
  .ag-header-group-cell {
    border-right: var(--ag-borders-critical) var(--ag-border-color) !important;
  }

  .inspection-dashboard-space::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  .inspection-dashboard-space .ag-body-vertical-scroll,
  .inspection-dashboard-space .ag-body-vertical-scroll .ag-body-vertical-scroll-viewport,
  .inspection-dashboard-space
    .ag-body-vertical-scroll
    .ag-body-vertical-scroll-viewport
    .ag-body-vertical-scroll-container {
    width: 15px !important;
    min-width: 15px !important;
    max-width: 15px !important;
  }

  .inspection-dashboard-space .ag-body-horizontal-scroll,
  .inspection-dashboard-space .ag-body-horizontal-scroll .ag-body-horizontal-scroll-viewport,
  .inspection-dashboard-space
    .ag-body-horizontal-scroll
    .ag-body-horizontal-scroll-viewport
    .ag-body-horizontal-scroll-container {
    height: 15px !important;
    min-height: 15px !important;
    max-height: 15px !important;
  }

  .inspection-dashboard-space
    .ag-body-vertical-scroll
    .ag-body-vertical-scroll-viewport::-webkit-scrollbar {
    width: 15px;
    height: 15px;
  }

  .inspection-dashboard-space
    .ag-body-horizontal-scroll
    .ag-body-horizontal-scroll-viewport::-webkit-scrollbar {
    width: 15px;
    height: 15px;
  }
  .ag-ltr .ag-cell,
  .ag-header-cell,
  .ag-header-group-cell {
    border-right: none !important;
  }
  .ag-root-wrapper {
    border: none !important;
    border-radius: 7px;
    min-height: 400px;
  }
  .ag-pinned-left-header {
    border-right: var(--ag-borders-critical) var(--v-grey-500-lighten3);
  }

  .ag-header,
  .ag-advanced-filter-header {
    background-color: var(--v-colored_bg-base);
    border-bottom: none;
    /* margin-bottom: 10px; */
    box-shadow: 0px 0px 20px 0px #0000000d;
  }
  .ag-row {
    border-bottom: 5px solid #0000000d;
    // box-shadow: 0px 0px 20px 0px #0000000d;
  }

  .ag-body {
    flex: 0 1 auto;
  }
  .gridContainer {
    flex-grow: 1;
  }

  .customSelectBox {
    border: 1px solid var(--v-primary-base);
    // &:hover {
    //   border: 1px solid var(--v-primary-base) !important;
    // }
    .v-input__control {
      .v-input__slot {
        box-shadow: none !important;
        // background: transparent !important;
      }
      .v-input__append-inner {
        .v-icon {
          color: var(--v-primary-base);
        }
      }
    }
    .selectedWord {
      color: var(--v-primary-base);
    }
  }

  .ag-overlay-wrapper.ag-layout-normal.ag-overlay-no-rows-wrapper {
    backdrop-filter: blur(1px);
    background: rgb(255 255 255 / 5%);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    pointer-events: none;
  }

  .ag-body.ag-layout-normal {
    flex: auto;
  }
</style>
